import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Collapse,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  limit,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { useAuth } from "../../../shared/auth/AuthProvider";
import { db } from "../../../shared/libs/firebaseConfig";

function toDate(v) {
  return v?.toDate?.() || null;
}

function formatDateTime(v) {
  const d = toDate(v) || v;
  if (!d) return "-";
  return d.toLocaleString("th-TH", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getRisk(row) {
  const max = Math.max(row.desktop || 0, row.mobile || 0, row.tablet || 0);
  if (max >= 5) return { text: "สูง", variant: "danger" };
  if (max >= 3) return { text: "เสี่ยง", variant: "warning" };
  return { text: "ปกติ", variant: "success" };
}

async function loadUserMap(uids = []) {
  const result = new Map();

  await Promise.all(
    uids.map(async (uid) => {
      try {
        const snap = await getDoc(doc(db, "users", uid));
        if (snap.exists()) result.set(uid, snap.data());
      } catch (e) {
        console.warn("load user failed:", uid, e?.message);
      }
    })
  );

  return result;
}

function formatUserTitle(row) {
  return row.email || row.phoneNumber || row.displayName || "ไม่พบข้อมูลผู้ใช้";
}

function slotLabel(slot) {
  if (slot === "desktop") return "Desktop";
  if (slot === "mobile") return "Mobile";
  if (slot === "tablet") return "Tablet";
  return slot || "Unknown";
}

export default function DeviceMonitorPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [errMsg, setErrMsg] = useState("");
  const [expandedUid, setExpandedUid] = useState("");

  const loadData = async () => {
    try {
      setLoading(true);
      setErrMsg("");

      const deviceSnap = await getDocs(
        query(
          collectionGroup(db, "deviceHistory"),
          orderBy("lastSeenAt", "desc"),
          limit(1000)
        )
      );

      const map = new Map();

      deviceSnap.docs.forEach((d) => {
        const data = d.data();
        const uid = data.uid || d.ref.parent.parent?.id;
        if (!uid) return;

        if (!map.has(uid)) {
          map.set(uid, {
            uid,
            desktop: 0,
            mobile: 0,
            tablet: 0,
            total: 0,
            lastSeenAt: null,
            devices: [],
          });
        }

        const row = map.get(uid);
        const slot = String(data.slot || "");

        if (slot === "desktop") row.desktop += 1;
        if (slot === "mobile") row.mobile += 1;
        if (slot === "tablet") row.tablet += 1;

        row.total += 1;

        const lastSeen = toDate(data.lastSeenAt);
        if (lastSeen && (!row.lastSeenAt || lastSeen > row.lastSeenAt)) {
          row.lastSeenAt = lastSeen;
        }

        row.devices.push({
          id: d.id,
          ...data,
          firstSeenDate: toDate(data.firstSeenAt),
          lastSeenDate: toDate(data.lastSeenAt),
          warningAckDate: toDate(data.warningAcknowledgedAt),
        });
      });

      const baseRows = Array.from(map.values());
      const userMap = await loadUserMap(baseRows.map((r) => r.uid));

      const enrichedRows = baseRows.map((row) => {
        const u = userMap.get(row.uid) || {};

        return {
          ...row,
          email: u.email || "",
          phoneNumber: u.phoneNumber || u.enrollProfile?.phoneNumber || "",
          displayName: u.displayName || "",
          accessControlOverride: u.accessControlOverride || null,
          devices: [...row.devices].sort((a, b) => {
            const ta = a.lastSeenDate?.getTime?.() || 0;
            const tb = b.lastSeenDate?.getTime?.() || 0;
            return tb - ta;
          }),
        };
      });

      setRows(enrichedRows);
    } catch (e) {
      console.error(e);
      setErrMsg(e?.message || "โหลดข้อมูลอุปกรณ์ไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const maxA = Math.max(a.desktop, a.mobile, a.tablet);
      const maxB = Math.max(b.desktop, b.mobile, b.tablet);
      if (maxB !== maxA) return maxB - maxA;

      const timeA = a.lastSeenAt?.getTime?.() || 0;
      const timeB = b.lastSeenAt?.getTime?.() || 0;
      return timeB - timeA;
    });
  }, [rows]);

  const enableStrictLock = async (uid) => {
    const ok = window.confirm(
        "ยืนยันจำกัดสิทธิเปลี่ยนอุปกรณ์ 14 วันสำหรับผู้ใช้นี้?"
    );
    if (!ok) return;

    await setDoc(
        doc(db, "users", uid),
        {
        accessControlOverride: {
            mode: "strict",
            enforceDeviceSlots: true,
            deviceLockDays: 14,
            updatedAt: serverTimestamp(),
            updatedBy: user?.uid || "",
        },
        },
        { merge: true }
    );

    await loadData();
    };

    const disableStrictLock = async (uid) => {
    const ok = window.confirm(
        "ยืนยันยกเลิกจำกัดอุปกรณ์ 14 วันสำหรับผู้ใช้นี้?"
    );
    if (!ok) return;

    await setDoc(
        doc(db, "users", uid),
        {
        accessControlOverride: {
            mode: "normal",
            enforceDeviceSlots: false,
            deviceLockDays: 14,
            updatedAt: serverTimestamp(),
            updatedBy: user?.uid || "",
        },
        },
        { merge: true }
    );

    await loadData();
    };

    const resetUserAccessDevices = async (uid) => {
    const ok = window.confirm(
        "ยืนยันรีเซ็ตอุปกรณ์ที่ผูกสิทธิ์ 14 วันของผู้ใช้นี้?\n\nหมายเหตุ: จะรีเซ็ตเฉพาะ accessDevices ไม่ลบ deviceHistory"
    );
    if (!ok) return;

    const snap = await getDocs(collection(db, "users", uid, "accessDevices"));

    await Promise.all(
        snap.docs.map((d) =>
        deleteDoc(doc(db, "users", uid, "accessDevices", d.id))
        )
    );

    await loadData();
    };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">ตรวจสอบอุปกรณ์ผู้ใช้งาน</h4>
          <div className="text-secondary small">
            แสดงจำนวนอุปกรณ์ที่บันทึกจากระบบ Device Monitoring
          </div>
        </div>

        <Button variant="outline-primary" onClick={loadData} disabled={loading}>
          รีโหลดข้อมูล
        </Button>
      </div>

      {errMsg ? <Alert variant="danger">{errMsg}</Alert> : null}

      <Card className="app-card">
        <Card.Body>
          {loading ? (
            <div className="py-5 text-center text-secondary">
              <Spinner animation="border" size="sm" /> กำลังโหลดข้อมูล...
            </div>
          ) : sortedRows.length === 0 ? (
            <Alert variant="info" className="mb-0">
              ยังไม่มีข้อมูล deviceHistory
            </Alert>
          ) : (
            <Table responsive bordered hover className="align-middle mb-0">
              <thead>
                <tr>
                  <th>User</th>
                  <th className="text-center">desktop</th>
                  <th className="text-center">mobile</th>
                  <th className="text-center">tablet</th>
                  <th className="text-center">risk</th>
                </tr>
              </thead>

              <tbody>
                {sortedRows.map((row) => {
                  const risk = getRisk(row);
                  const open = expandedUid === row.uid;

                  return (
                    <React.Fragment key={row.uid}>
                      <tr
                        style={{ cursor: "pointer" }}
                        onClick={() => setExpandedUid(open ? "" : row.uid)}
                      >
                        <td>
                          <div className="fw-semibold">{formatUserTitle(row)}</div>
                          <div className="small text-secondary text-break">
                            UID: {row.uid}
                          </div>
                          <div className="small text-secondary">
                            รวม {row.total} อุปกรณ์ • ล่าสุด{" "}
                            {formatDateTime(row.lastSeenAt)}
                          </div>
                          <div className="small text-primary mt-1">
                            {open ? "ซ่อนรายละเอียด ▲" : "ดูรายละเอียด ▼"}
                          </div>
                        </td>

                        <td className="text-center">{row.desktop}</td>
                        <td className="text-center">{row.mobile}</td>
                        <td className="text-center">{row.tablet}</td>

                        <td className="text-center">
                          <Badge bg={risk.variant}>{risk.text}</Badge>
                        </td>
                      </tr>

                      <tr>
                        <td colSpan={5} className="p-0 border-0">
                          <Collapse in={open}>
                            <div className="p-3 bg-light border-start border-end border-bottom">
                              <div className="d-flex flex-wrap gap-2 mb-3">
                                {row.accessControlOverride?.enforceDeviceSlots ? (
                                    <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        disableStrictLock(row.uid);
                                    }}
                                    >
                                    ยกเลิกจำกัดอุปกรณ์ 14 วัน
                                    </Button>
                                ) : (
                                    <Button
                                    size="sm"
                                    variant="outline-warning"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        enableStrictLock(row.uid);
                                    }}
                                    >
                                    จำกัดสิทธิเปลี่ยนอุปกรณ์ 14 วัน
                                    </Button>
                                )}

                                <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={(e) => {
                                    e.stopPropagation();
                                    resetUserAccessDevices(row.uid);
                                    }}
                                >
                                    รีเซ็ตอุปกรณ์ที่ใช้งาน
                                </Button>

                                {row.accessControlOverride?.enforceDeviceSlots ? (
                                    <Badge bg="warning" text="dark" className="align-self-center">
                                    Strict Lock เปิดอยู่
                                    </Badge>
                                ) : (
                                    <Badge bg="secondary" className="align-self-center">
                                    ไม่ได้จำกัดรายบุคคล
                                    </Badge>
                                )}
                                </div>
                              {row.devices.length === 0 ? (
                                <Alert variant="info" className="mb-0">
                                  ไม่มีรายละเอียดอุปกรณ์
                                </Alert>
                              ) : (
                                <div className="d-flex flex-column gap-3">
                                  {row.devices.map((device) => (
                                    <Card key={device.id} className="border">
                                      <Card.Body>
                                        <div className="d-flex justify-content-between flex-wrap gap-2 mb-2">
                                          <div>
                                            <div className="fw-semibold">
                                              {slotLabel(device.slot)}
                                            </div>
                                            <div className="small text-secondary text-break">
                                              {device.label || "-"}
                                            </div>
                                          </div>

                                          <div>
                                            {device.warningAcknowledgedAt ? (
                                              <Badge bg="secondary">
                                                รับทราบคำเตือนแล้ว
                                              </Badge>
                                            ) : (
                                              <Badge bg="light" text="dark">
                                                ยังไม่เคยรับทราบคำเตือน
                                              </Badge>
                                            )}
                                          </div>
                                        </div>

                                        <Table size="sm" bordered responsive className="mb-0">
                                          <tbody>
                                            <tr>
                                              <td style={{ width: 180 }}>
                                                เข้าใช้ครั้งแรก
                                              </td>
                                              <td>{formatDateTime(device.firstSeenDate)}</td>
                                            </tr>
                                            <tr>
                                              <td>เข้าใช้ล่าสุด</td>
                                              <td>{formatDateTime(device.lastSeenDate)}</td>
                                            </tr>
                                            <tr>
                                              <td>จำนวนครั้งที่ตรวจพบ</td>
                                              <td>{device.seenCount || 0} ครั้ง</td>
                                            </tr>
                                            <tr>
                                              <td>Platform</td>
                                              <td>{device.platform || "-"}</td>
                                            </tr>
                                            <tr>
                                              <td>Screen</td>
                                              <td>{device.screenSize || "-"}</td>
                                            </tr>
                                            <tr>
                                              <td>Timezone</td>
                                              <td>{device.timezone || "-"}</td>
                                            </tr>
                                            <tr>
                                              <td>รับทราบคำเตือน</td>
                                              <td>
                                                {formatDateTime(device.warningAckDate)}
                                              </td>
                                            </tr>
                                            <tr>
                                              <td>Signature</td>
                                              <td className="text-break small">
                                                {device.signature || device.id}
                                              </td>
                                            </tr>
                                          </tbody>
                                        </Table>

                                        <details className="mt-2">
                                          <summary className="small text-secondary">
                                            User Agent
                                          </summary>
                                          <div className="small text-secondary text-break mt-1">
                                            {device.userAgent || "-"}
                                          </div>
                                        </details>
                                      </Card.Body>
                                    </Card>
                                  ))}
                                </div>
                              )}
                            </div>
                          </Collapse>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}